import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Sparkle } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const TaxOptimizer = () => {
  const [profile, setProfile] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [withdrawalAmount, setWithdrawalAmount] = useState(50000);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, accountsRes] = await Promise.all([
        api.get('/financial-profile'),
        api.get('/retirement-accounts')
      ]);
      setProfile(profileRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  // Simplified tax calculation (educational purposes)
  const calculateTaxes = (income, filingStatus = 'single') => {
    const brackets2024Single = [
      { max: 11000, rate: 0.10 },
      { max: 44725, rate: 0.12 },
      { max: 95375, rate: 0.22 },
      { max: 182100, rate: 0.24 },
      { max: 231250, rate: 0.32 },
      { max: 578125, rate: 0.35 },
      { max: Infinity, rate: 0.37 }
    ];

    let tax = 0;
    let remaining = income;
    let previousMax = 0;

    for (const bracket of brackets2024Single) {
      const taxableInBracket = Math.min(remaining, bracket.max - previousMax);
      tax += taxableInBracket * bracket.rate;
      remaining -= taxableInBracket;
      previousMax = bracket.max;
      if (remaining <= 0) break;
    }

    return Math.round(tax);
  };

  // Withdrawal strategies
  const strategies = [
    {
      name: 'Traditional First',
      description: 'Withdraw from Traditional accounts first',
      taxableIncome: withdrawalAmount,
      taxes: calculateTaxes(withdrawalAmount)
    },
    {
      name: 'Balanced',
      description: 'Mix of Traditional (60%) and Roth (40%)',
      taxableIncome: withdrawalAmount * 0.6,
      taxes: calculateTaxes(withdrawalAmount * 0.6)
    },
    {
      name: 'Tax-Optimized',
      description: 'Fill lower tax brackets strategically',
      taxableIncome: Math.min(withdrawalAmount, 44725),
      taxes: calculateTaxes(Math.min(withdrawalAmount, 44725))
    }
  ];

  return (
    <div className="space-y-8 fade-in">
      {/* Educational Disclaimer */}
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Tool Only:</strong> Tax calculations are simplified estimates for educational purposes. Tax laws are complex and vary by individual circumstances. Always consult a qualified tax professional or CPA for personalized tax advice.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Tax-Aware Withdrawal Optimizer
        </h1>
        <p className="text-base text-[#4A5568]">
          Compare withdrawal strategies to minimize tax burden (Educational estimates only)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-6">Withdrawal Amount</h2>
            
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-3">
                  <Label>Annual Withdrawal</Label>
                  <span className="text-lg font-semibold text-[#C5A059] ibm-plex">${withdrawalAmount.toLocaleString()}</span>
                </div>
                <Slider
                  value={[withdrawalAmount]}
                  onValueChange={(value) => setWithdrawalAmount(value[0])}
                  min={10000}
                  max={200000}
                  step={5000}
                  data-testid="withdrawal-amount-slider"
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-[#4A5568] mt-1">
                  <span>$10K</span>
                  <span>$200K</span>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-[#4A5568] leading-relaxed">
                  <strong>Note:</strong> Estimates based on 2024 federal tax brackets for single filers. State taxes, deductions, and credits not included.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Strategy Comparison */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="tax-strategies-comparison">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Withdrawal Strategies</h2>
            
            <div className="space-y-4 mb-6">
              {strategies.map((strategy, idx) => (
                <div key={idx} className="border border-slate-200 rounded-md p-4" data-testid={`strategy-${idx}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-[#0A1128]">{strategy.name}</h3>
                      <p className="text-sm text-[#4A5568]">{strategy.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#4A5568]">Est. Taxes</p>
                      <p className="text-xl font-semibold text-[#C53030] ibm-plex">${strategy.taxes.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-slate-100">
                    <span className="text-[#4A5568]">Taxable Income</span>
                    <span className="font-medium text-[#1A202C] ibm-plex">${strategy.taxableIncome.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4A5568]">After-Tax Amount</span>
                    <span className="font-medium text-[#2D6A4F] ibm-plex">${(withdrawalAmount - strategy.taxes).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>

            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={strategies}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#4A5568" style={{ fontSize: '12px' }} />
                <YAxis stroke="#4A5568" />
                <Tooltip />
                <Legend />
                <Bar dataKey="taxes" name="Estimated Taxes" fill="#C53030" />
                <Bar dataKey="taxableIncome" name="Taxable Income" fill="#0A1128" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxOptimizer;